import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import moment from 'moment';
import { VehicleImageCard } from 'components/vehicle-history';
import { useSwipeTabs } from 'hooks/useSwipeTabs';
import { useCounterAmount, useLastOfferAmount } from '../hooks/useVehicleOffers';
import { Link } from 'react-router-dom';
import { PAYMENT_METHODS_STATUSES, VEHICLE_STATUSES } from 'constants/index';
import PricingBlock from './PricingBlock';
import PaymentHistoryBlock from './PaymentHistoryBlock';
import VehicleTitleBlock from './VehicleTitleBlock';
import BuyerDocsIcon from '../icons/all_docs.svg';
import VinIcon from '../icons/vin.svg';
import MileageIcon from '../icons/mileage.svg';
import BuyerIcon from '../icons/seller.svg';
import ReservePriceIcon from '../icons/reserve_price.svg';
import DateSoldIcon from '../icons/date_sold.svg';
import AwardPriceIcon from '../icons/award_price.svg';

interface MobileTransactionCardProps {
  vehicle: any;
}

const TABS = ['Vehicle', 'Payment', 'Title'] as const;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const VehicleTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-top: 10px;
  line-height: 1.3;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const InfoContent = styled.div`
  display: flex;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #000;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #000;
`;

const VehicleIdBold = styled.span`
  font-weight: 700;
`;

const VehicleIdSlug = styled.span`
  font-weight: 400;
`;

const AwardedLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #4caf50;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  letter-spacing: 0.3px;
`;

const TabBar = styled.div`
  display: flex;
  border-bottom: 2px solid #e0e0e0;
  margin-top: 12px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 0;
  background: none;
  border: none;
  border-bottom: 3px solid ${({ $active }) => ($active ? '#005a96' : 'transparent')};
  margin-bottom: -2px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  color: ${({ $active }) => ($active ? '#005a96' : '#666')};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
`;

const slideInFromRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInFromLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const TabContent = styled.div<{ $direction: 'left' | 'right' | null }>`
  margin-top: 12px;
  ${({ $direction }) =>
    $direction === 'left' &&
    css`
      animation: ${slideInFromRight} 0.25s ease-out;
    `}
  ${({ $direction }) =>
    $direction === 'right' &&
    css`
      animation: ${slideInFromLeft} 0.25s ease-out;
    `}
`;

const DocLinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const DocLink = styled.a`
  font-size: 14px;
  font-weight: 700;
  color: #225a91;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const DocLinkRouter = styled(Link)`
  font-size: 14px;
  font-weight: 700;
  color: #225a91;
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const TitleStatusLabel = styled.div<{ $bg: string }>`
  width: 100%;
  margin-top: 16px;
  padding: 14px;
  background: ${({ $bg }) => $bg};
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  height: 44px;
  color: #fff;
  text-align: center;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const formatPrice = (amount: number | string | null | undefined): string => {
  if (!amount) return 'N/A';
  return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const getTitleStatus = (vehicle: any): { label: string; bg: string } => {
  const tr = vehicle.transferRecord;
  if (tr?.status === 'title_issues') return { label: 'Title Issues', bg: '#AF2930' };
  if (tr?.date_title_received || tr?.x_title_inn === 'X') return { label: 'Title Received', bg: '#459B4A' };
  return { label: 'Waiting on Title', bg: '#CA8D57' };
};

const MobileTransactionCard = ({ vehicle }: MobileTransactionCardProps) => {
  const history = useHistory();
  const { activeIndex, goToTab, handlers, slideDirection, clearSlideDirection } = useSwipeTabs(TABS.length);

  const isAwarded = vehicle.status === 'awarded';
  const vehicleTitle = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  const imageUrl = vehicle.preview_image?.url;
  const auctionId = vehicle.auction?.slug ? `${vehicle.auction.slug}-${vehicle.orderIndex || '-'}` : null;

  const counterAmount = useCounterAmount(vehicle);
  const lastOfferAmount = useLastOfferAmount(vehicle);

  const reservePrice = formatPrice(vehicle.reserve_price);
  const dateSold = vehicle.transferRecord?.date_sold
    ? moment.utc(vehicle.transferRecord.date_sold).format('MM/DD/YYYY')
    : 'N/A';
  const buyerName = vehicle.awarded_user?.dealership_name ?? 'N/A';
  const titleStatus = getTitleStatus(vehicle);

  const renderInfoRows = () => (
    <InfoList>
      <InfoRow>
        <VinIcon />
        <InfoContent>
          <InfoLabel>VIN:&nbsp;</InfoLabel>
          <InfoValue>{vehicle.vin}</InfoValue>
        </InfoContent>
      </InfoRow>

      <InfoRow>
        <ReservePriceIcon />
        <InfoContent>
          <InfoValue>Reserve:&nbsp;</InfoValue>
          <InfoLabel>{reservePrice}</InfoLabel>
        </InfoContent>
      </InfoRow>

      <InfoRow>
        {isAwarded ? <DateSoldIcon /> : <ReservePriceIcon />}
        <InfoContent>
          {isAwarded ? (
            <>
              <InfoValue>Date Sold:&nbsp;</InfoValue>
              <InfoLabel>{dateSold}</InfoLabel>
            </>
          ) : (
            <>
              <InfoValue>Counter:&nbsp;</InfoValue>
              <InfoLabel>{formatPrice(counterAmount)}</InfoLabel>
            </>
          )}
        </InfoContent>
      </InfoRow>

      <InfoRow>
        <AwardPriceIcon />
        <InfoContent>
          {isAwarded ? (
            <>
              <InfoValue>Award Price:&nbsp;</InfoValue>
              <InfoLabel>{formatPrice(vehicle.vehicle_price)}</InfoLabel>
            </>
          ) : (
            <>
              <InfoValue>Last Offer:&nbsp;</InfoValue>
              <InfoLabel>{formatPrice(lastOfferAmount)}</InfoLabel>
            </>
          )}
        </InfoContent>
      </InfoRow>

      <InfoRow>
        <MileageIcon />
        <InfoContent>
          <InfoLabel>{vehicle.mileage?.toLocaleString()}&nbsp;</InfoLabel>
          <InfoValue>/Mileage</InfoValue>
        </InfoContent>
      </InfoRow>

      <InfoRow>
        <BuyerIcon />
        <InfoContent>
          <InfoLabel>{buyerName}</InfoLabel>
        </InfoContent>
      </InfoRow>
    </InfoList>
  );

  const renderTabContent = () => {
    switch (activeIndex) {
      case 0:
        return (
          <>
            {renderInfoRows()}
            {vehicle.status === VEHICLE_STATUSES.AWARDED && (
              <DocLinksRow>
                <BuyerDocsIcon />
                <DocLinkRouter to={`/vehicles/${vehicle.id}/buyer-docs`}>Buyer Docs</DocLinkRouter>
                {vehicle.seller_report_of_sale && (
                  <DocLink href={vehicle.seller_report_of_sale} target="_blank" rel="noopener noreferrer">
                    Report of Sale
                  </DocLink>
                )}
                {vehicle.payment_status === PAYMENT_METHODS_STATUSES.APPROVED && (
                  <DocLink href={`/vehicles/${vehicle.id}/funding-notice`} target="_blank" rel="noopener noreferrer">
                    Funding Notice
                  </DocLink>
                )}
              </DocLinksRow>
            )}
          </>
        );
      case 1:
        return (
          <>
            <PricingBlock vehicle={vehicle} />
            <div style={{ height: 24 }} />
            <PaymentHistoryBlock vehicle={vehicle} />
            <TitleStatusLabel $bg={titleStatus.bg}>{titleStatus.label}</TitleStatusLabel>
          </>
        );
      case 2:
        return <VehicleTitleBlock vehicle={vehicle} />;
      default:
        return null;
    }
  };

  return (
    <Card>
      <VehicleImageCard
        imageUrl={imageUrl}
        label={
          auctionId && (
            <>
              <VehicleIdBold>Vehicle ID: </VehicleIdBold>
              <VehicleIdSlug>{auctionId}</VehicleIdSlug>
            </>
          )
        }
        onClick={() => history.push(`/vehicles/${vehicle.id}/view`)}
        badge={isAwarded ? <AwardedLabel>Awarded</AwardedLabel> : undefined}
      />

      <CardBody>
        <VehicleTitle>{vehicleTitle}</VehicleTitle>

        {!isAwarded ? (
          renderInfoRows()
        ) : (
          <>
            <TabBar>
              {TABS.map((tab, i) => (
                <Tab key={tab} $active={activeIndex === i} onClick={() => goToTab(i)}>
                  {tab}
                </Tab>
              ))}
            </TabBar>

            <TabContent
              key={activeIndex}
              $direction={slideDirection}
              onAnimationEnd={clearSlideDirection}
              {...handlers}
            >
              {renderTabContent()}
            </TabContent>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default MobileTransactionCard;
